import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Import/Export button cluster used at the top of admin list tabs.
 *
 * @param entity  one of "bookings" | "customers" | "drivers" | "routes"
 * @param onImported callback to refresh data after import
 */
export default function ImportExportButtons({ entity, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    try {
      const sessionToken = localStorage.getItem("session_token");
      const res = await fetch(`${BACKEND_URL}/api/admin/export/${entity}?format=${format}`, {
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `taxinearme_${entity}_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${entity} (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    }
  };

  const downloadTemplate = async () => {
    try {
      const sessionToken = localStorage.getItem("session_token");
      const res = await fetch(`${BACKEND_URL}/api/admin/import/${entity}/template?format=xlsx`, {
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `template_${entity}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Template download failed: ${e.message || e}`);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(csv|xlsx)$/i.test(file.name)) { toast.error("Please upload .csv or .xlsx"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" },
      });
      const { created = 0, updated = 0, skipped = 0, errors = [] } = res.data || {};
      const summary = `Imported ${entity}: ${created} created, ${updated} updated, ${skipped} skipped`;
      if (errors.length) {
        toast.error(`${summary}. ${errors.length} errors. First: ${errors[0]}`);
      } else {
        toast.success(summary);
      }
      onImported && onImported(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`import-export-${entity}`}>
      <Button variant="ghost" size="sm" onClick={() => download("xlsx")} className="text-white/70 hover:text-[#C5A059]" data-testid={`export-${entity}-xlsx`}>
        <Download className="w-3.5 h-3.5 mr-1" /> Export XLSX
      </Button>
      <Button variant="ghost" size="sm" onClick={() => download("csv")} className="text-white/60 hover:text-[#C5A059]" data-testid={`export-${entity}-csv`}>
        <Download className="w-3.5 h-3.5 mr-1" /> CSV
      </Button>
      <Button variant="ghost" size="sm" onClick={downloadTemplate} className="text-white/40 hover:text-[#C5A059]" data-testid={`template-${entity}`}>
        <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Template
      </Button>
      <label className="cursor-pointer">
        <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onFile} className="hidden" disabled={busy} data-testid={`import-${entity}-input`} />
        <span className="inline-flex items-center text-xs px-3 py-1.5 rounded-sm bg-white/5 border border-white/10 text-white/80 hover:bg-[#C5A059]/10 hover:border-[#C5A059]/40 transition-colors" data-testid={`import-${entity}-btn`}>
          <Upload className="w-3.5 h-3.5 mr-1" /> {busy ? "Importing..." : "Import"}
        </span>
      </label>
    </div>
  );
}
